import type { BillingReport } from '../lib/api';
import {
  formatHours,
  formatRateFromCents,
  formatUsdFromCents,
} from '../lib/format';

interface Props {
  report: BillingReport;
}

const KIND_LABELS: Record<BillingReport['line_items'][number]['kind'], string> = {
  standard: 'Standard (wall-clock)',
  human: 'Human time',
  agent: 'Agent time',
};

export function BillingCard({ report }: Props) {
  const allRatesNull = report.line_items.every((i) => i.rate_cents === null);

  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-sub font-medium tracking-sub text-black">
          Billing
        </h2>
        <div className="font-display text-feature font-medium tracking-feature text-midnight tabular-nums">
          {formatUsdFromCents(report.total_cents)}
        </div>
      </div>

      <div className="card overflow-hidden">
        {allRatesNull ? (
          <div className="p-5">
            <p className="font-display text-body text-black/60 tracking-body">
              No rates configured. Click <strong>Edit</strong> above to set an hourly
              rate, or attach a client that provides defaults.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border-light">
            {report.line_items.map((item) => (
              <li
                key={item.kind}
                className="px-5 py-4 flex items-center justify-between gap-6"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="badge">{item.kind}</span>
                  <span className="font-display text-body text-black">
                    {KIND_LABELS[item.kind]}
                  </span>
                </div>
                <div className="flex items-center gap-6 text-right shrink-0">
                  <span className="font-mono text-[11px] text-black/50 tabular-nums min-w-[60px]">
                    {formatHours(item.hours)}
                  </span>
                  <span className="font-mono text-[11px] text-black/50 tabular-nums min-w-[70px]">
                    {formatRateFromCents(item.rate_cents)}
                  </span>
                  <span className="font-display text-body font-medium tabular-nums min-w-[80px]">
                    {formatUsdFromCents(item.amount_cents)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <RateSourceHint report={report} />
    </section>
  );
}

function RateSourceHint({ report }: { report: BillingReport }) {
  const inherits: string[] = [];
  if (report.effective_rates.standard_source === 'client') inherits.push('standard');
  if (report.effective_rates.human_source === 'client') inherits.push('human');
  if (report.effective_rates.agent_source === 'client') inherits.push('agent');
  if (inherits.length === 0) return null;
  return (
    <p className="mt-3 font-display text-caption text-black/50">
      {inherits.join(', ')} rate inherited from the linked client.
    </p>
  );
}
